"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeRecipientData = sanitizeRecipientData;
/**
 * Sanitizes recipient data to ensure it passes Mangopay schema validation.
 * Replaces invalid characters with spaces and trims the results.
 *
 * This function uses validation patterns based on Mangopay's official API documentation.
 *
 * @see https://docs.mangopay.com/api-reference/recipients/recipient-object
 * @see https://docs.mangopay.com/api-reference/recipients/view-recipient-schema
 *
 * @param recipientData The recipient data to sanitize
 * @returns Sanitized recipient data that should pass Mangopay validation
 */
function sanitizeRecipientData(recipientData) {
    const sanitizedData = { ...recipientData };
    // Sanitize DisplayName - Pattern: "^(?!.*[&,'/]).{1,50}$"
    // Cannot contain: &,'/  |  Max length: 50
    if (sanitizedData.DisplayName) {
        sanitizedData.DisplayName = sanitizeString(sanitizedData.DisplayName, /[&,'/]/g, 50);
    }
    if (sanitizedData.BusinessRecipient) {
        const businessRecipient = { ...sanitizedData.BusinessRecipient };
        // BusinessName - Pattern: "^(?!.*[()&,.:_/]).{1,255}$"
        // Cannot contain: ()&,.:_/  |  Max length: 255
        if (businessRecipient.BusinessName) {
            businessRecipient.BusinessName = sanitizeString(businessRecipient.BusinessName, /[()&,.:_/]/g, 255);
        }
        if (businessRecipient.Address) {
            businessRecipient.Address = sanitizeAddress(businessRecipient.Address);
        }
        sanitizedData.BusinessRecipient = businessRecipient;
    }
    if (sanitizedData.IndividualRecipient) {
        const individualRecipient = { ...sanitizedData.IndividualRecipient };
        // FirstName and LastName - Pattern: "^(?!.*[()&,.:_/]).{1,255}$"
        // Cannot contain: ()&,.:_/  |  Max length: 255 (apostrophes are allowed in names)
        if (individualRecipient.FirstName) {
            individualRecipient.FirstName = sanitizeString(individualRecipient.FirstName, /[()&,.:_/]/g, 255);
        }
        if (individualRecipient.LastName) {
            individualRecipient.LastName = sanitizeString(individualRecipient.LastName, /[()&,.:_/]/g, 255);
        }
        if (individualRecipient.Address) {
            individualRecipient.Address = sanitizeAddress(individualRecipient.Address);
        }
        sanitizedData.IndividualRecipient = individualRecipient;
    }
    return sanitizedData;
}
function sanitizeAddress(address) {
    const sanitizedAddress = { ...address };
    // AddressLine1 and AddressLine2 - Pattern: "^(?!.*[()/]).{1,255}$"
    // Cannot contain: ()/  |  Max length: 255
    if (sanitizedAddress.AddressLine1) {
        sanitizedAddress.AddressLine1 = sanitizeString(sanitizedAddress.AddressLine1, /[()/]/g, 255);
    }
    if (sanitizedAddress.AddressLine2) {
        sanitizedAddress.AddressLine2 = sanitizeString(sanitizedAddress.AddressLine2, /[()/]/g, 255);
    }
    // City - Pattern varies by country, common restrictions: &,.:_'
    // Cannot contain: &,.:_'  |  Max length: 80
    if (sanitizedAddress.City) {
        sanitizedAddress.City = sanitizeString(sanitizedAddress.City, /[&,.:_']/g, 80);
    }
    // Region - Pattern varies by country, common restrictions: &,.:_'-/
    // Cannot contain: &,.:_'/  |  Max length: 50
    if (sanitizedAddress.Region) {
        sanitizedAddress.Region = sanitizeString(sanitizedAddress.Region, /[&,.:_'/]/g, 50);
    }
    // PostalCode - Pattern: "^(?!.*[()&,.:_'/]).{1,10}$"
    // Cannot contain: ()&,.:_'/  |  Max length: 10
    if (sanitizedAddress.PostalCode) {
        sanitizedAddress.PostalCode = sanitizeString(sanitizedAddress.PostalCode, /[()&,.:_'/]/g, 10);
    }
    return sanitizedAddress;
}
function sanitizeString(value, invalidCharsRegex, maxLength) {
    if (!value) {
        return value;
    }
    let sanitized = value.replace(invalidCharsRegex, " ");
    sanitized = sanitized.replace(/\s+/g, " ");
    sanitized = sanitized.trim();
    if (sanitized.length > maxLength) {
        sanitized = sanitized.substring(0, maxLength).trim();
    }
    return sanitized;
}
//# sourceMappingURL=sanitizeRecipientData.js.map